import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { saveDateOfBirth } from '../api';
import { signOut } from '../auth';
import { Field } from '../components/Field';
import { Spinner } from '../components/Spinner';

// Year/month/day age math, UTC, completed years. Mirrors the server-side
// computation so the client gives the same answer the server will enforce.
function ageYearsClient(dobIso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dobIso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const now = new Date();
  let years = now.getUTCFullYear() - y;
  if (now.getUTCMonth() < mo || (now.getUTCMonth() === mo && now.getUTCDate() < d)) years--;
  if (years < 0 || years > 130) return null;
  return years;
}

// Today, ISO YYYY-MM-DD — used as the date input's max so users can't pick the future.
function todayIso(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function AgeGate() {
  const navigate = useNavigate();
  const [dob, setDob] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientAge = dob ? ageYearsClient(dob) : null;
  const clientUnder18 = clientAge !== null && clientAge < 18;
  const canSubmit = dob.length === 10 && !submitting && !clientUnder18;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    // Guards against firing PUT /users/me with a malformed or missing dob.
    // The button's disabled state covers the click path, but Enter-key
    // submissions and browser autofill quirks can bypass that — and the
    // server has no graceful path for an empty body, so the result is a
    // 400 the user can't recover from cleanly. Belt-and-braces: same
    // validity check the button uses, but enforced at submit time too.
    if (submitting) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      setError('Please pick a valid date of birth.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await saveDateOfBirth(dob);
      navigate('/welcome', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      // Server-side under-18 path: account already deleted server-side. Sign out
      // locally and bounce back to /sign-in with a banner.
      if (msg.toLowerCase().includes('18 or older')) {
        try {
          await signOut();
        } catch {
          // Best-effort — the account is already gone server-side; a failed
          // remote revoke shouldn't strand the user on the age gate showing
          // an unrelated fetch error instead of the deletion banner below.
        }
        navigate('/sign-in', { replace: true, state: { banner: msg } });
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="shell welcome" onSubmit={onSubmit}>
      <header className="welcome__hero rise">
        <div className="brand"><span className="brand__dot" />RESLT</div>
        <h1 className="welcome__title">One quick check.</h1>
        <p className="welcome__sub">
          We need to confirm you're 18 or older before we can set up your coaching plan.
          We don't share or display this — it stays on your account.
        </p>
      </header>

      <section className="section rise">
        <Field
          label="Date of birth"
          hint={clientUnder18 ? "You need to be 18 or older to use RESLT." : undefined}
        >
          <input
            className="input"
            type="date"
            required
            max={todayIso()}
            value={dob}
            onChange={(e) => setDob(e.target.value)}
          />
        </Field>
        {error ? <div className="errorbar">{error}</div> : null}
        <button
          type="submit"
          className="btn btn--primary btn--lg btn--full"
          disabled={!canSubmit}
        >
          {submitting ? (<><Spinner /> Saving…</>) : 'Continue'}
        </button>
        <p className="faint" style={{ fontSize: 12, textAlign: 'center', marginTop: 4 }}>
          By continuing you accept the{' '}
          <Link to="/terms" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
            Terms of Service
          </Link>.
        </p>
      </section>
    </form>
  );
}
